import { useCallback, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { ToastContext, type ToastKind } from './toastContext';

// Lightweight success/error toast surface for mutation feedback (A1EE-338).
// The context + useToast hook live in ./toastContext so this file exports only
// the ToastProvider component (react-refresh/only-export-components).

type ToastEntry = { id: number; message: string; kind: ToastKind };

// 7s, not 5s: for a page like /me a toast is the ONLY confirmation the action worked (the
// page is otherwise byte-identical before and after a reconnect), so it has to survive the
// moment a user spends re-reading the unchanged page before glancing at the corner.
const TOAST_TTL_MS = 7000;

export function ToastProvider({ children }: { children: ReactNode }): ReactElement {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, kind: ToastKind = 'success') => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, message, kind }]);
      // No need to clear this timer when a toast is dismissed by hand: the callback filters by
      // id, so firing after a manual dismiss is a no-op on an already-absent entry.
      setTimeout(() => dismiss(id), TOAST_TTL_MS);
      return id;
    },
    [dismiss],
  );

  // Both callbacks are stable, so this pins the context value identity too — consumers
  // re-render when their own state changes, not on every ToastProvider render.
  const value = useMemo(() => ({ showToast, dismissToast: dismiss }), [showToast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed bottom-4 right-4 z-[60] flex flex-col gap-2"
      >
        {/*
          DARK fill, OPAQUE border, white text. This was `bg-green-50`/`bg-red-50` with dark
          text — a near-white panel on a near-white page (`green-50` is oklch(98.2%) against the
          shell's `slate-50` oklch(98.4%): 0.2 percentage points of lightness, chroma .018). It
          rendered correctly and was still unreadable as an alert, so a real "GitHub credentials
          refreshed" toast was reported as "no toast at all" (A1EE-692).

          NO TEST CAN CATCH THAT REGRESSION: jsdom applies no CSS, so RTL asserting on toast
          text passes for any palette. If you restyle this, check it in a browser against
          `bg-slate-50` — and keep white `text-sm` at WCAG AA (>=4.5:1) on the *composited*
          fill, which is what the alpha makes non-obvious:

            green-700/90 over slate-50 -> #2c8c50, 4.22:1  FAILS
            green-900/70 over slate-50 -> #58856b, 4.21:1  FAILS
            green-800/80 over slate-50 -> #43835c, 4.53:1  marginal
            green-900/75 over slate-50 -> #4d7d61, 4.75:1  ok   <- used
            red-800/75   over slate-50 -> #b15353, 4.96:1  ok   <- used
        */}
        {toasts.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => dismiss(t.id)}
            title="Click to dismiss"
            className={
              'pointer-events-auto max-w-sm cursor-pointer rounded border px-4 py-2 text-left ' +
              'text-sm font-medium text-white shadow-lg backdrop-blur-md ' +
              (t.kind === 'error'
                ? 'border-red-950 bg-red-800/75 hover:bg-red-800'
                : 'border-green-950 bg-green-900/75 hover:bg-green-900')
            }
          >
            {t.message}
          </button>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
